import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Repeat, 
  Play, 
  Pause, 
  Trash2, 
  Calendar, 
  CalendarDays, 
  RotateCcw,
  Plus,
  AlertCircle,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { Button, Modal } from '../components/common';
import { showToast } from '../components/common/Toast';
import { Header } from '../components/layout/Header';
import { useUIStore } from '../stores/uiStore';
import type { Task, RecurringRule } from '../types';

const RecurringTasksPage: React.FC = () => {
  const [templates, setTemplates] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [deleteInstances, setDeleteInstances] = useState(false);
  const { openTaskModal } = useUIStore();
  const { t } = useTranslation();

  const fetchRecurringTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/tasks/recurring', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data);
      } else {
        setError(data.error?.message || t('recurring.error.failedToLoad'));
      }
    } catch (err) {
      setError(t('recurring.error.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecurringTemplates();

    // Listen for recurring task creation/update events from TaskForm
    const handleRecurringTaskCreated = () => {
      fetchRecurringTemplates();
    };
    const handleRecurringTaskUpdated = () => {
      fetchRecurringTemplates();
    };

    window.addEventListener('recurringTaskCreated', handleRecurringTaskCreated);
    window.addEventListener('recurringTaskUpdated', handleRecurringTaskUpdated);

    return () => {
      window.removeEventListener('recurringTaskCreated', handleRecurringTaskCreated);
      window.removeEventListener('recurringTaskUpdated', handleRecurringTaskUpdated);
    };
  }, []);

  const handlePause = async (taskId: string) => {
    try {
      const response = await fetch(`/api/tasks/recurring/${taskId}/pause`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        await fetchRecurringTemplates();
      }
    } catch (err) {
      console.error('Failed to pause recurring task:', err);
      showToast(t('recurring.error.failedToPause'), 'error');
    }
  };

  const handleResume = async (taskId: string) => {
    try {
      const response = await fetch(`/api/tasks/recurring/${taskId}/resume`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        await fetchRecurringTemplates();
      }
    } catch (err) {
      console.error('Failed to resume recurring task:', err);
      showToast(t('recurring.error.failedToResume'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    
    try {
      const response = await fetch(`/api/tasks/recurring/${selectedTask.id}?deleteInstances=${deleteInstances}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        setDeleteModalOpen(false);
        setSelectedTask(null);
        setDeleteInstances(false);
        await fetchRecurringTemplates();
      }
    } catch (err) {
      console.error('Failed to delete recurring task:', err);
      showToast(t('recurring.error.failedToDelete'), 'error');
    }
  };

  const openDeleteModal = (task: Task) => {
    setSelectedTask(task);
    setDeleteInstances(false);
    setDeleteModalOpen(true);
  };

  const getFrequencyIcon = (frequency?: string) => {
    switch (frequency) {
      case 'DAILY':
        return <Calendar className="w-4 h-4" />;
      case 'WEEKLY':
        return <RotateCcw className="w-4 h-4" />;
      case 'MONTHLY':
        return <CalendarDays className="w-4 h-4" />;
      default:
        return <Repeat className="w-4 h-4" />;
    }
  };

  const getFrequencyLabel = (rule?: RecurringRule) => {
    if (!rule) return t('recurring.unknown');
    
    let label = '';
    switch (rule.frequency) {
      case 'DAILY':
        label = rule.interval === 1 ? t('recurring.daily') : t('recurring.everyDays', { interval: rule.interval });
        break;
      case 'WEEKLY':
        const dayNames = [t('recurring.daySun'), t('recurring.dayMon'), t('recurring.dayTue'), t('recurring.dayWed'), t('recurring.dayThu'), t('recurring.dayFri'), t('recurring.daySat')];
        if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
          const days = rule.daysOfWeek.map(d => dayNames[d]).join(', ');
          label = rule.interval === 1 ? t('recurring.weeklyOn', { days }) : t('recurring.everyWeeksOn', { interval: rule.interval, days });
        } else {
          label = rule.interval === 1 ? t('recurring.weekly') : t('recurring.everyWeeks', { interval: rule.interval });
        }
        break;
      case 'MONTHLY':
        label = rule.interval === 1 ? t('recurring.monthlyOnDay', { day: rule.dayOfMonth || 1 }) : t('recurring.everyMonthsOnDay', { interval: rule.interval, day: rule.dayOfMonth || 1 });
        break;
    }
    
    return label;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">{t('recurring.loading')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title={t('recurring.title')} />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('recurring.subtitle')}
          </p>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => openTaskModal(undefined, { isRecurring: true })}
          >
            {t('recurring.newRecurringTask')}
          </Button>
        </div>

        {/* Empty State */}
        {!isLoading && templates.length === 0 && (
          <div className="text-center py-12">
            <Repeat className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('recurring.emptyTitle')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('recurring.emptySubtitle')}
            </p>
            <Button
              variant="primary"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => openTaskModal(undefined, { isRecurring: true })}
            >
              {t('recurring.createRecurringTask')}
          </Button>
        </div>
      )}

      {/* Templates List */}
      {templates.length > 0 && (
        <div className="space-y-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`
                p-4 rounded-lg border transition-all hover:shadow-md
                ${template.recurringRule?.isPaused 
                  ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800' 
                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800'
                }
              `}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`
                  p-2 rounded-lg
                  ${template.recurringRule?.isPaused 
                    ? 'bg-amber-100 dark:bg-amber-900/30' 
                    : 'bg-primary-100 dark:bg-primary-900/30'
                  }
                `}>
                  {getFrequencyIcon(template.recurringRule?.frequency)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {template.title}
                    </h3>
                    {template.recurringRule?.isPaused && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {t('recurring.paused')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {getFrequencyLabel(template.recurringRule)}
                    </span>
                    
                    {template.project && (
                      <span
                        className="px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${template.project.color}20`, color: template.project.color }}
                      >
                        {template.project.name}
                      </span>
                    )}
                    
                    {template._count?.recurringInstances !== undefined && (
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {t('recurring.instancesCreated', { count: template._count.recurringInstances })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openTaskModal(template.id)}
                    className="px-3 py-1.5 min-h-[44px] text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                  >
                    {t('common.edit')}
                  </button>
                  
                  {template.recurringRule?.isPaused ? (
                    <button
                      onClick={() => handleResume(template.id)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 min-h-[44px] text-sm rounded-lg bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors"
                    >
                      <Play className="w-3 h-3" />
                      {t('recurring.resume')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePause(template.id)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 min-h-[44px] text-sm rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50 transition-colors"
                    >
                      <Pause className="w-3 h-3" />
                      {t('recurring.pause')}
                    </button>
                  )}
                  
                  <button
                    onClick={() => openDeleteModal(template)}
                    className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    title={t('recurring.deleteRecurringTask')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedTask(null);
          setDeleteInstances(false);
        }}
        title={t('recurring.deleteRecurringTaskTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('recurring.deleteConfirmation', { title: selectedTask?.title })}
          </p>
          
          <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
            <input
              type="checkbox"
              checked={deleteInstances}
              onChange={(e) => setDeleteInstances(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('recurring.alsoDeleteInstances')}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('recurring.instancesWillBeDeleted', { count: selectedTask?._count?.recurringInstances || 0 })}
              </p>
            </div>
          </label>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedTask(null);
                setDeleteInstances(false);
              }}
              className="w-full sm:w-auto"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              className="w-full sm:w-auto"
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
      </main>
    </div>
  );
};

export default RecurringTasksPage;